import React, { useState } from 'react';
import { Zap, CheckCircle, Shield, TrendingUp, ArrowRight } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function OnboardingScreen({ currentStep = 1 }) {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleConnectGoogleAds = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/auth/google/start`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.url) {
        // Redirect to Google OAuth
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to start connection');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error connecting Google Ads:', err);
      setError('Failed to connect. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{ 
        maxWidth: '600px', 
        width: '100%',
        textAlign: 'center'
      }}>
        {/* Progress Indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '3rem'
        }}>
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: step === currentStep 
                  ? theme.colors.brand.primary
                  : step < currentStep
                    ? theme.colors.status.success
                    : theme.colors.bg.tertiary,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '1.1rem',
                transition: 'all 0.3s'
              }}>
                {step < currentStep ? '✓' : step}
              </div>
              {step < 3 && (
                <div style={{
                  width: '40px',
                  height: '2px',
                  background: step < currentStep 
                    ? theme.colors.status.success 
                    : theme.colors.bg.tertiary
                }} />
              )}
            </div>
          ))}
        </div>

        {/* Step Label */}
        <div style={{
          fontSize: '0.9rem',
          color: theme.colors.text.tertiary,
          marginBottom: '1rem',
          fontWeight: '600',
          letterSpacing: '0.05em',
          textTransform: 'uppercase'
        }}>
          Step {currentStep} of 3
        </div>

        {/* Main Icon */}
        <div style={{
          width: '100px',
          height: '100px',
          margin: '0 auto 2rem',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          borderRadius: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 10px 40px rgba(99, 102, 241, 0.3)'
        }}>
          <Zap size={48} color="white" />
        </div>

        {/* Heading */}
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: theme.colors.text.primary,
          marginBottom: '1rem',
          lineHeight: '1.2'
        }}>
          Welcome to Bidoryx! 🚀
        </h1>

        {/* Subtitle */}
        <p style={{
          fontSize: '1.2rem',
          color: theme.colors.text.secondary,
          marginBottom: '3rem',
          lineHeight: '1.6'
        }}>
          Let's connect your Google Ads account to get started.<br />
          <span style={{ fontSize: '1rem', color: theme.colors.text.tertiary }}>
            Takes only 30 seconds
          </span>
        </p>

        {/* CTA Button */}
        <button
          onClick={handleConnectGoogleAds}
          disabled={loading}
          style={{
            width: '100%',
            padding: '1.25rem 2rem',
            background: loading 
              ? theme.colors.bg.tertiary
              : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '16px',
            fontSize: '1.2rem',
            fontWeight: '700',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            boxShadow: loading ? 'none' : '0 8px 25px rgba(99, 102, 241, 0.4)',
            transition: 'all 0.3s',
            marginBottom: '2rem'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(99, 102, 241, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(99, 102, 241, 0.4)';
          }}
        >
          {loading ? (
            <>
              <div className="spinner" />
              Connecting...
            </>
          ) : (
            <>
              🔗 Connect Google Ads
              <ArrowRight size={24} />
            </>
          )}
        </button>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '1rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${theme.colors.status.error}`,
            borderRadius: '12px',
            color: theme.colors.status.error,
            marginBottom: '2rem',
            fontSize: '0.95rem'
          }}>
            {error}
          </div>
        )}

        {/* Features */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.5rem',
          marginTop: '3rem'
        }}>
          {[
            { icon: Shield, label: 'Secure OAuth 2.0', color: '#10b981' },
            { icon: CheckCircle, label: 'Read-only access', color: '#6366f1' },
            { icon: TrendingUp, label: 'Disconnect anytime', color: '#8b5cf6' }
          ].map((feature, idx) => (
            <div
              key={idx}
              style={{
                padding: '1.5rem 1rem',
                background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: '12px',
                textAlign: 'center'
              }}
            >
              <feature.icon 
                size={32} 
                style={{ 
                  color: feature.color,
                  marginBottom: '0.75rem'
                }} 
              />
              <div style={{
                fontSize: '0.85rem',
                color: theme.colors.text.secondary,
                fontWeight: '500',
                lineHeight: '1.4'
              }}>
                {feature.label}
              </div>
            </div>
          ))}
        </div>

        {/* Help Text */}
        <p style={{
          marginTop: '3rem',
          fontSize: '0.9rem',
          color: theme.colors.text.tertiary,
          lineHeight: '1.6'
        }}>
          By connecting, you'll be able to manage campaigns, view analytics,<br />
          and use AI-powered optimization features.
        </p>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spinner {
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
      `}</style>
    </div>
  );
}

